import React, { useState, useEffect, useRef } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, TextInput, ScrollView } from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { useSelector } from 'react-redux';

const chartOptions = [
  { id: 1, name: 'Current 15 Min', key: 'current15Min' },
  { id: 2, name: 'Total Today', key: 'today' },
  { id: 3, name: '/min Today', key: 'userPerformance' },
  { id: 4, name: '/min Yest. vs Today', key: 'yesterdayVsToday' },
  { id: 5, name: 'Past 6 Days', key: 'thisWeek' },
  { id: 6, name: '/min (6 Days)', key: 'itemsPerMinute' },
  { id: 7, name: '/Hour', key: 'itemsPerHour' },
  { id: 8, name: "Today's High Scores", key: 'scanToScore' },
  { id: 9, name: 'Daily Order Goal', key: 'dailyOrderGoal' }
];

const defaultSettings = {
  current15Min: { enabled: false, time: 15, autoTime: true, metric: 'items' },
  today: { enabled: true, time: 15, autoTime: true, metric: 'items' },
  userPerformance: { enabled: true, time: 15, autoTime: true, metric: 'items' },
  yesterdayVsToday: { enabled: true, time: 15, autoTime: true, metric: 'items' },
  thisWeek: { enabled: true, time: 15, autoTime: true, metric: 'items' },
  itemsPerMinute: { enabled: true, time: 15, autoTime: true, metric: 'items' },
  itemsPerHour: { enabled: true, time: 15, autoTime: true, metric: 'items' },
  scanToScore: { enabled: true, time: 15, autoTime: true, metric: 'items' },
  dailyOrderGoal: {enabled: true, time: 15, autoTime: true, metric: 'orders'}
};

const getScanScoreUserKey = score => {
  return score.user_id || score.packing_user_id || score.userId;
};

const SettingsDropdown = ({ isOpen, onClose, updateDashboardSettings, activeChart }) => {
  const [settings, setSettings] = useState(defaultSettings);
  const [timeInputs, setTimeInputs] = useState({});
  const dropdownRef = useRef(null);
  const [hasChanges, setHasChanges] = useState(false);
  
  const listDaily = useSelector(state => state.statistics.listDaily);
  const withIn15MinList = useSelector(state => state.statistics.withIn15MinList);
  const todayVsYesterdayList = useSelector(state => state.statistics.todayVsYesterdayList);
  const weeklyList = useSelector(state => state.statistics.weeklyList);
  const itemsPerMinuteList = useSelector(state => state.statistics.itemsPerMinuteList);
  const itemsPerHourList = useSelector(state => state.statistics.itemsPerHourList);
  const scanScoreDailyList = useSelector(state => state.statistics.scanScoreDailyList);
  const scanScoreList = useSelector(state => state.statistics.scanScoreList);
  
  const [legendItems, setLegendItems] = useState([]);

  const getUserDetailsByActiveChart = () => {
    if (activeChart === 2 || activeChart === 3) {
      return listDaily?.data?.user_details || [];
    }
    else if (activeChart === 1) {
      return withIn15MinList?.data?.user_details || [];
    }
    else if (activeChart === 4) {
      return todayVsYesterdayList?.data?.user_details || [];
    }
    else if (activeChart === 5) {
      return weeklyList?.data?.user_details || [];
    }
    else if (activeChart === 6) {
      return itemsPerMinuteList?.data?.user_details || [];
    }
    else if (activeChart === 7) {
      return itemsPerHourList?.data?.user_details || [];
    }
    else if (activeChart === 8) {
      return scanScoreDailyList?.data?.user_details || [];
    }

    return [];
  };

  useEffect(() => {
    const userDetails = getUserDetailsByActiveChart();
    
    const sortedUsers = [...userDetails].sort((a, b) => 
      (a.name || '').localeCompare(b.name || '')
    );
    
    setLegendItems(sortedUsers);
  }, [
    activeChart,
    listDaily?.data?.user_details,
    withIn15MinList?.data?.user_details,
    todayVsYesterdayList?.data?.user_details,
    weeklyList?.data?.user_details,
    itemsPerMinuteList?.data?.user_details,
    itemsPerHourList?.data?.user_details,
    scanScoreDailyList?.data?.statistics,
    scanScoreDailyList?.data?.user_details,
    scanScoreList
  ]);

  useEffect(() => {
    const loadSettings = async () => {
      try {
        const storedSettings = await AsyncStorage.getItem('chartSettings');
        if (storedSettings) {
          const parsedSettings = JSON.parse(storedSettings);

          const mergedSettings = {...defaultSettings};
          Object.keys(parsedSettings).forEach(key => {
            mergedSettings[key] = {
              ...defaultSettings[key],
              ...parsedSettings[key]
            };
            if (mergedSettings[key].autoTime === undefined) {
              mergedSettings[key].autoTime = true;
            }
            if (!mergedSettings[key].metric) {
              mergedSettings[key].metric = 'items';
            }
          });
          
          setSettings(mergedSettings);
          const initialInputs = {};
          Object.keys(mergedSettings).forEach(key => {
            initialInputs[key] = mergedSettings[key].time.toString();
          });
          setTimeInputs(initialInputs);
        } else {
          setSettings(defaultSettings);
          
          const initialInputs = {};
          Object.keys(defaultSettings).forEach(key => {
            initialInputs[key] = defaultSettings[key].time.toString();
          });
          setTimeInputs(initialInputs);
        }
      } catch (error) {
        console.error('Error loading settings:', error);
      }
    };

    loadSettings();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target) && isOpen) {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose]);

  useEffect(() => {
    setHasChanges(true);
  }, [settings]);

  useEffect(() => {
    if (!isOpen && hasChanges) {
      const autoSave = async () => {
        try {
          Object.keys(timeInputs).forEach(key => {
            const numValue = Math.min(Math.max(parseInt(timeInputs[key]) || 5, 5), 60);
            setSettings(prev => ({
              ...prev,
              [key]: {
                ...prev[key],
                time: numValue,
                autoTime: prev[key].autoTime
              }
            }));
          });
          
          await AsyncStorage.setItem('chartSettings', JSON.stringify(settings));
          updateDashboardSettings(settings);
          setHasChanges(false);
        } catch (error) {
          console.error('Error auto-saving on close:', error);
        }
      };
      
      autoSave();
    }
  }, [isOpen, hasChanges, settings, timeInputs, updateDashboardSettings]);

  const toggleMetric = (key) => {
    setSettings(prevSettings => ({
      ...prevSettings,
      [key]: {
        ...prevSettings[key],
        metric: prevSettings[key].metric === 'orders' ? 'items' : 'orders'
      }
    }));
  };

  const handleToggleOption = (key) => {
    setSettings((prevSettings) => ({
      ...prevSettings,
      [key]: {
        ...prevSettings[key],
        enabled: !prevSettings[key].enabled
      }
    }));
  };

  const handleTimeInputChange = (key, value) => {
    if (/^\d*$/.test(value)) {
      setTimeInputs(prev => ({
        ...prev,
        [key]: value
      }));
      
      if (settings[key]?.autoTime) {
        setSettings(prevSettings => ({
          ...prevSettings,
          [key]: {
            ...prevSettings[key],
            autoTime: false
          }
        }));
      }
    }
  };

  const finalizeTimeInput = (key) => {
    const numValue = Math.min(Math.max(parseInt(timeInputs[key]) || 5, 5), 90);
    setSettings((prevSettings) => ({
      ...prevSettings,
      [key]: {
        ...prevSettings[key],
        time: numValue,
        autoTime: false
      }
    }));
    setTimeInputs(prev => ({
      ...prev,
      [key]: numValue.toString()
    }));
  };

  const handleKeyPress = (e, key) => {
    if (e.key === 'Enter') {
      finalizeTimeInput(key);
    }
  };

  const saveChanges = async () => {
    try {
      Object.keys(timeInputs).forEach(key => {
        finalizeTimeInput(key);
      });
      await AsyncStorage.setItem('chartSettings', JSON.stringify(settings));
      updateDashboardSettings(settings);
      setHasChanges(false);
      onClose();
    } catch (error) {
      console.error('Error saving settings:', error);
    }
  };

  if (!isOpen) return null;

  return (
    <View style={styles.dropdownContainer} ref={dropdownRef} testID="settings-dropdown">
      <View style={styles.header} testID="settings-header">
        <View style={styles.headerContent}>
          <i className="icon-cog" style={styles.settingsIcon}></i>
          <Text style={styles.headerText} testID="settings-title">Settings</Text>
        </View>
      </View>
      
      <ScrollView style={styles.scrollContainer} testID="settings-scroll">
        <View style={styles.optionsContainer}>
          {chartOptions.map((option) => (
            <View key={option.id} style={styles.optionRow} testID={`option-row-${option.key}`}>
              {option.key === 'scanToScore' ? (
                <View style={styles.metricToggleSpacer} />
              ) : (
                <TouchableOpacity
                  testID={`metric-toggle-${option.key}`}
                  onPress={() => toggleMetric(option.key)}
                  style={[
                    styles.metricToggleButton,
                    !settings[option.key].enabled && styles.disabledButton,
                    settings[option.key].metric === 'orders' && styles.metricToggleButtonActive
                  ]}
                  disabled={!settings[option.key].enabled}
                >
                  <Text
                    style={[
                      styles.metricToggleText,
                      settings[option.key].metric === 'orders' && styles.metricToggleTextActive
                    ]}
                  >
                    {settings[option.key].metric === 'orders' ? 'Orders' : 'Items'}
                  </Text>
                </TouchableOpacity>
              )}
              <Text style={styles.optionText} testID={`option-text-${option.key}`}>
                {option.name}
              </Text>
              <TouchableOpacity
                testID={`auto-button-${option.key}`}
                onPress={() => {
                  const newAutoTimeState = !settings[option.key].autoTime;
                  setSettings(prevSettings => ({
                    ...prevSettings,
                    [option.key]: {
                      ...prevSettings[option.key],
                      autoTime: newAutoTimeState
                    }
                  }));
                }}
                style={[
                  styles.autoButton,
                  settings[option.key].autoTime && styles.activeAutoButton,
                  !settings[option.key].enabled && styles.disabledButton
                ]}
                disabled={!settings[option.key].enabled}
              >
                <Text style={[
                  styles.autoTimeText,
                  settings[option.key].autoTime && styles.activeButtonText
                ]}>
                  Auto
                </Text>
              </TouchableOpacity>
              <View style={styles.inputContainer} testID={`input-container-${option.key}`}>
                <TextInput
                  testID={`time-input-${option.key}`}
                  style={[
                    styles.timeInput,
                    !settings[option.key].autoTime && styles.activeTimeInput,
                    !settings[option.key].enabled && styles.disabledInput
                  ]}
                  value={timeInputs[option.key] || settings[option.key].time.toString()}
                  onChangeText={(value) => {
                    handleTimeInputChange(option.key, value);
                  }}
                  onBlur={() => finalizeTimeInput(option.key)}
                  onKeyPress={(e) => handleKeyPress(e, option.key)}
                  keyboardType="numeric"
                  maxLength={2}
                  editable={settings[option.key].enabled}
                />
                <Text style={styles.secText}>sec</Text>
              </View>
              
              <TouchableOpacity
                testID={`checkbox-${option.key}`}
                onPress={() => handleToggleOption(option.key)}
                style={styles.checkboxContainer}
              >
                {settings[option.key].enabled ? (
                  <i className="icon-check" style={styles.checkIcon}></i>
                ) : (
                  <i className="icon-check-empty" style={styles.checkEmptyIcon}></i>
                )}
              </TouchableOpacity>
            </View>
          ))}
        </View>
        
        {legendItems.length > 0 && (
          <View style={styles.legendSection} testID="legend-section">
            <Text style={styles.legendTitle} testID="legend-title">Users</Text>
            <View style={styles.legendContainer}>
              <View style={styles.legendBackground}>
                <View style={styles.legendContent} testID="legend-content">
                  {legendItems.map((user, index) => (
                    <View key={`legend-${index}`} style={styles.legendItem} testID={`legend-item-${index}`}>
                      <View
                        style={[styles.legendColor, { backgroundColor: user.color }]}
                        testID={`legend-color-${index}`}
                      />
                      <Text style={styles.legendText} numberOfLines={1} ellipsizeMode="tail" testID={`legend-text-${index}`}>
                        {user.name}
                      </Text>
                    </View>
                  ))}
                </View>
              </View>
            </View>
          </View>
        )}
      </ScrollView>
      
      <TouchableOpacity style={styles.saveButton} onPress={saveChanges} testID="save-button">
        <Text style={styles.saveButtonText}>Save Changes</Text>
      </TouchableOpacity>
    </View>
  );
};

const styles = StyleSheet.create({
  dropdownContainer: {
    position: 'absolute',
    backgroundColor: '#262B30',
    borderRadius: 10,
    right: 20,
    top: 50,
    zIndex: 1000,
    overflow: 'hidden',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.25,
    shadowRadius: 3.84,
    elevation: 5,
    maxHeight: '80%',
  },
  scrollContainer: {
    maxHeight: '550',
  },
  header: {
    backgroundColor: '#0E1317',
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(255, 255, 255, 0.1)',
    paddingVertical: 16,
    paddingHorizontal: 20,
  },
  headerContent: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
  },
  settingsIcon: {
    color: 'white',
    fontSize: 22,
    marginRight: 10,
  },
  headerText: {
    color: 'white',
    fontSize: 18,
    fontFamily: 'Poppins_600SemiBold',
  },
  optionsContainer: {
    paddingVertical: 10,
  },
  optionRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: 12,
    paddingHorizontal: 20,
  },
  optionText: {
    color: 'white',
    fontSize: 16,
    fontFamily: 'Poppins_600SemiBold',
    flex: 1,
  },
  metricToggleButton: {
    backgroundColor: '#1A1D20',
    borderRadius: 20,
    paddingHorizontal: 12,
    height: 36,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 10,
    borderWidth: 1,
    borderColor: 'transparent'
  },
  metricToggleSpacer: {
    width: 70,
    marginRight: 10
  },
  metricToggleButtonActive: {
    borderColor: '#E618FF'
  },
  metricToggleText: {
    color: 'white',
    fontSize: 14,
    fontFamily: 'Poppins_600SemiBold'
  },
  metricToggleTextActive: {
    color: 'white'
  },
  inputContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginRight: 20,
  },
  timeInput: {
    backgroundColor: '#1A1D20',
    color: 'white',
    width: 50,
    height: 36,
    borderRadius: 20,
    textAlign: 'center',
    fontFamily: 'Poppins_600SemiBold',
    fontSize: 16,
    paddingHorizontal: 0,
    borderWidth: 1,
    borderColor: 'transparent',
  },
  activeTimeInput: {
    borderColor: '#E618FF',
  },
  disabledInput: {
    opacity: 0.5,
  },
  secText: {
    color: 'white',
    marginLeft: 8,
    fontFamily: 'Poppins_600SemiBold',
  },
  autoButton: {
    backgroundColor: '#1A1D20',
    width: 60,
    height: 36,
    borderRadius: 20,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 15,
    borderWidth: 1,
    borderColor: 'transparent',
    marginLeft: 5,
  },
  activeAutoButton: {
    borderColor: '#E618FF',
  },
  disabledButton: {
    opacity: 0.5,
  },
  autoTimeText: {
    color: 'white',
    fontSize: 14,
    fontFamily: 'Poppins_600SemiBold',
    textAlign: 'center',
  },
  activeButtonText: {
    color: 'white',
  },
  checkboxContainer: {
    width: 24,
    height: 24,
    justifyContent: 'center',
    alignItems: 'center',
  },
  checkIcon: {
    color: 'white',
    fontSize: 24,
  },
  checkEmptyIcon: {
    color: 'white',
    fontSize: 24,
  },
  legendSection: {
    paddingTop: 15,
    borderTopWidth: 1,
    borderTopColor: 'rgba(255, 255, 255, 0.1)',
    paddingHorizontal: 20,
    paddingBottom: 10,
  },
  legendTitle: {
    color: 'white',
    fontSize: 16,
    fontFamily: 'Poppins_600SemiBold',
    marginBottom: 10,
  },
  legendContainer: {
    marginBottom: 15,
  },
  legendBackground: {
    backgroundColor: '#0E1317',
    borderRadius: 8,
    padding: 10,
  },
  legendContent: {
    paddingVertical: 5,
  },
  legendItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 8,
  },
  legendColor: {
    width: 16,
    height: 16,
    borderRadius: 3,
    marginRight: 10,
  },
  legendText: {
    color: 'white',
    fontSize: 14,
    fontFamily: 'Poppins_600SemiBold',
    flex: 1,
  },
  saveButton: {
    backgroundColor: '#1A1D20',
    marginVertical: 16,
    paddingVertical: 12,
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: '#fff',
    borderRadius: 30,
    justifyContent: 'center',
    alignItems: 'center',
    height: 30,
    shadowColor: '#e853fa',
    shadowOffset: {width: 0, height: 0.5},
    shadowRadius: 8,
    elevation: 5,
    shadowOpacity: 0.5,
    width: 150,
    marginHorizontal: 'auto'
  },
  saveButtonText: {
    color: 'white',
    fontSize: 16,
    fontFamily: 'Poppins_600SemiBold',
  },
});

export default SettingsDropdown;
